import React from 'react';
import { motion } from 'framer-motion';
import { FaBox, FaTruck, FaMapMarkerAlt, FaCreditCard, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';
import {cardFloat} from "../animations/animations";

const OrderCard = ({
                       orderId,
                       orderNumber,
                       items = [],
                       address = {},
                       paymentMethod = 'razorpay',
                       total = 0,
                       estimatedDelivery = new Date(),
                       orderStatus = 'confirmed',
                       paymentStatus = 'completed'
                   }) => {
    const formatDate = (date) => {
        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) {
                return 'Date not available';
            }
            return dateObj.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            return 'Date not available';
        }
    };

    const formatCurrency = (amount) => {
        if (typeof amount !== 'number' || isNaN(amount)) {
            return '₹0.00';
        }
        return `₹${amount.toFixed(2)}`;
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'completed':
            case 'confirmed':
                return 'bg-sage text-white';
            case 'processing':
                return 'bg-champagne text-white';
            case 'pending':
                return 'bg-amber-500 text-white';
            case 'failed':
                return 'bg-error text-white';
            default:
                return 'bg-nude/30 text-charcoal';
        }
    };

    const getStatusText = (status) => {
        switch(status) {
            case 'completed': return 'Payment Completed';
            case 'confirmed': return 'Order Confirmed';
            case 'processing': return 'Processing';
            case 'pending': return 'Payment Pending';
            case 'failed': return 'Payment Failed';
            default: return status || 'Processing';
        }
    };

    return (
        <motion.div
            variants={cardFloat}
            className="luxury-card overflow-hidden"
        >
            {/* Header */}
            <div className="p-6 border-b border-nude/30 bg-gradient-to-r from-champagne/5 to-transparent">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                            <FaBox className="w-5 h-5 text-champagne" />
                        </div>
                        <div>
                            <h3 className="font-medium text-charcoal">Order Confirmation</h3>
                            <p className="text-sm text-charcoal/50">#{orderNumber || orderId?.slice(-8) || 'N/A'}</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-light text-charcoal">{formatCurrency(total)}</div>
                        <p className="text-xs text-charcoal/40">Total Amount</p>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {/* Status Badge */}
                <div className="mb-6 flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(paymentStatus)}`}>
                        {getStatusText(paymentStatus)}
                    </span>
                    <span className="text-sm text-charcoal/50">
                        Order ID: {orderId?.slice(-8) || 'N/A'}
                    </span>
                </div>

                {/* Items */}
                <div className="mb-6">
                    <h4 className="font-medium text-charcoal mb-4 flex items-center gap-2">
                        <span className="w-2 h-2 bg-champagne rounded-full"></span>
                        Items Ordered
                    </h4>
                    {items.length > 0 ? (
                        <div className="space-y-3">
                            {items.map((item, index) => (
                                <motion.div
                                    key={item._id || item.id || index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-nude/5"
                                >
                                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                                        {item.image ? (
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-nude/30 flex items-center justify-center">
                                                <FaBox className="w-6 h-6 text-charcoal/30" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-charcoal">{item.name || 'Product'}</p>
                                        <p className="text-sm text-charcoal/50">
                                            {item.variant?.name || item.variant || 'Standard'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium text-charcoal">
                                            {formatCurrency(item.price || 0)}
                                        </p>
                                        <p className="text-xs text-charcoal/50">Qty: {item.quantity || 1}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-charcoal/40">
                            <FaBox className="w-12 h-12 mx-auto mb-3 opacity-20" />
                            <p>No items to display</p>
                        </div>
                    )}
                </div>

                {/* Details Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Delivery Info */}
                    <div>
                        <h4 className="font-medium text-charcoal mb-4 flex items-center gap-2">
                            <FaTruck className="w-4 h-4 text-champagne" />
                            Delivery Information
                        </h4>
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-charcoal/50 mb-1">Estimated Delivery</p>
                                <p className="font-medium text-charcoal">
                                    {formatDate(estimatedDelivery)}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-charcoal/50 mb-1 flex items-center gap-1">
                                    <FaMapMarkerAlt className="w-3 h-3" />
                                    Shipping Address
                                </p>
                                <div className="text-charcoal">
                                    {address.name ? (
                                        <>
                                            <p className="font-medium">{address.name}</p>
                                            <p>{address.address || 'Address not specified'}</p>
                                            <p>{address.city}, {address.state} {address.zipCode || address.zip}</p>
                                        </>
                                    ) : (
                                        <p className="text-charcoal/50">Address not available</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Info */}
                    <div>
                        <h4 className="font-medium text-charcoal mb-4 flex items-center gap-2">
                            <FaCreditCard className="w-4 h-4 text-champagne" />
                            Payment Details
                        </h4>
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-charcoal/50 mb-1">Payment Method</p>
                                <p className="font-medium text-charcoal capitalize">
                                    {paymentMethod === 'razorpay' ? 'Razorpay (UPI/Card)' :
                                        paymentMethod?.replace?.('-', ' ') || 'Online Payment'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-charcoal/50 mb-1 flex items-center gap-1">
                                    <FaCalendarAlt className="w-3 h-3" />
                                    Order Date
                                </p>
                                <p className="font-medium text-charcoal">{formatDate(new Date())}</p>
                            </div>
                            <div>
                                <p className="text-sm text-charcoal/50 mb-1">Payment Status</p>
                                <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${
                                        paymentStatus === 'completed' ? 'bg-sage' :
                                            paymentStatus === 'pending' ? 'bg-amber-500' : 'bg-error'
                                    }`}></div>
                                    <span className="font-medium text-charcoal capitalize">
                                        {paymentStatus === 'completed' ? 'Paid' : paymentStatus || 'Pending'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-nude/30 bg-gradient-to-b from-white to-pearl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full animate-pulse ${
                            orderStatus === 'confirmed' ? 'bg-champagne' :
                                orderStatus === 'processing' ? 'bg-amber-500' :
                                    orderStatus === 'shipped' ? 'bg-sage' : 'bg-nude/30'
                        }`}></div>
                        <p className="text-sm text-charcoal/50">
                            {orderStatus === 'confirmed' ? 'Processing your order' :
                                orderStatus === 'processing' ? 'Preparing for shipment' :
                                    orderStatus === 'shipped' ? 'Shipped and on the way' :
                                        'Order received'}
                        </p>
                    </div>
                    <p className="text-sm text-charcoal/50 text-center md:text-right">
                        {paymentStatus === 'completed' ?
                            'You\'ll receive a confirmation email with tracking details shortly' :
                            'Payment is being processed'}
                    </p>
                </div>
            </div>
        </motion.div>
    );
};

export default OrderCard;